import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { AuthData, AuthStage } from './types';
import axiosInstance from '../../utils/axiosConfig';
import { useData } from '../Data/DataContext';

interface AuthContextProps {
  authData: AuthData;
  setEmail: (email: string) => void;
  setCode: (code: string) => void;
  resetAuth: () => void;
  setStage: (stage: AuthStage) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | undefined) => void;
  checkAuthStatus: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth muss innerhalb eines AuthProviders verwendet werden');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { data, refreshData } = useData();

  // Initialer Zustand
  const initialState = (): AuthData => {
    return {
      email: '',
      isLoading: true,
      stage: AuthStage.EMAIL_INPUT
    };
  };

  const [authData, setAuthData] = useState<AuthData>(initialState);

  // Wir verwenden keinen useEffect hier, da wir den Initialzustand über checkAuthStatus verwalten

  // Prüft den Anmeldestatus beim Backend
  const checkAuthStatus = async (): Promise<boolean> => {
    try {
      setAuthData(prev => ({ ...prev, isLoading: true }));

      // Rufe die API auf, nur wenn es absolut notwendig ist
      // refreshData enthält jetzt eine Logik, die mehrfache API-Aufrufe verhindert
      console.log('checkAuthStatus: Prüfe Auth-Status');
      const result = await refreshData();
      const isLoggedIn = result?.logged_in === true;

      setAuthData(prev => ({
        ...prev,
        isLoading: false,
        stage: isLoggedIn ? AuthStage.AUTHENTICATED : AuthStage.EMAIL_INPUT
      }));

      return isLoggedIn;
    } catch (error) {
      console.error('Fehler beim Prüfen des Anmeldestatus:', error);
      setAuthData(prev => ({
        ...prev,
        isLoading: false,
        stage: AuthStage.EMAIL_INPUT,
        error: 'Fehler beim Prüfen des Anmeldestatus'
      }));
      return false;
    }
  };

  // Wir benötigen keinen useEffect hier, da der Authentifizierungsstatus direkt
  // vom checkAuthStatus verwaltet wird

  const setEmail = (email: string) => {
    setAuthData(prev => ({ ...prev, email }));
  };

  const setCode = (code: string) => {
    setAuthData(prev => ({ ...prev, code }));
  };

  const setLoading = (isLoading: boolean) => {
    setAuthData(prev => ({ ...prev, isLoading }));
  };

  const setError = (error: string | undefined) => {
    setAuthData(prev => ({ ...prev, error }));
  };

  const setStage = (stage: AuthStage) => {
    const prevStage = authData.stage;
    setAuthData(prev => ({ ...prev, stage }));

    // Nach erfolgreicher Authentifizierung sollten die Daten nur aktualisiert werden, 
    // wenn zuvor keine Daten vorhanden waren
    if (stage === AuthStage.AUTHENTICATED && prevStage !== AuthStage.AUTHENTICATED && !data) {
      console.log('AuthContext: Status auf AUTHENTICATED gewechselt, lade Daten');
      refreshData().catch(err => console.error('Fehler beim Laden der Daten:', err));
    }
  };

  const resetAuth = async () => {
    setAuthData(prev => ({ ...prev, isLoading: true }));
    try {
      // API-Aufruf zum Logout-Endpunkt
      await axiosInstance.post('/logout');
      setAuthData({
        email: '',
        isLoading: false,
        stage: AuthStage.EMAIL_INPUT
      });
      // Lokalen Speicher löschen
      localStorage.removeItem('authState');
    } catch (error) {
      console.error('Fehler beim Logout:', error);
      setAuthData(prev => ({
        ...prev,
        isLoading: false,
        error: 'Fehler beim Abmelden'
      }));
    }
  };

  return (
    <AuthContext.Provider value={{
      authData,
      setEmail,
      setCode,
      resetAuth,
      setStage,
      setLoading,
      setError,
      checkAuthStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
};
